import { useEffect, useState, type JSX } from 'react';
import { fetchOperatorProfile, updateOperatorProfile, type OperatorOrgProfile } from '../api';
import { Field, PortalSection, inputClass, primaryButtonClass } from './portalUi';

/**
 * Operator onboarding / org profile (Drone D1 #318) + the pilot's call-back phone (#346). Reads
 * + renames the operator's OWN agency and lets the acting pilot publish a phone number the ops
 * centre can call (surfaced on the gov mission detail as a tel: link). `kind` is shown read-only
 * — it is the tenant class the COP gate keys on and is NOT editable from the portal.
 */
export function ProfileSection(): JSX.Element {
  const [org, setOrg] = useState<OperatorOrgProfile | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [savedPhone, setSavedPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    void fetchOperatorProfile(ac.signal)
      .then((p) => {
        if (!alive || !p) return;
        setOrg(p.organization);
        setName(p.organization.name);
        setPhone(p.phone ?? '');
        setSavedPhone(p.phone ?? '');
      })
      .catch(() => {});
    return () => {
      alive = false;
      ac.abort();
    };
  }, []);

  const nameChanged = org !== null && name.trim().length > 0 && name.trim() !== org.name;
  const phoneChanged = phone.trim() !== savedPhone;
  const dirty = nameChanged || phoneChanged;

  async function save(): Promise<void> {
    if (!dirty || org === null) return;
    setSaving(true);
    setError(null);
    try {
      const input: { name?: string; phone?: string | null } = {};
      if (nameChanged) input.name = name.trim();
      if (phoneChanged) input.phone = phone.trim().length > 0 ? phone.trim() : null;
      const updated = await updateOperatorProfile(input);
      setOrg(updated.organization);
      setName(updated.organization.name);
      setPhone(updated.phone ?? '');
      setSavedPhone(updated.phone ?? '');
    } catch {
      setError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PortalSection title="Organization" testId="portal-profile">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Field label="Operator name">
            <input
              data-testid="profile-name-input"
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your organization's name"
            />
          </Field>
        </div>
        <div className="flex-1">
          <Field label="Call-back phone (for the EOC)">
            <input
              data-testid="profile-phone-input"
              className={inputClass}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. +1-807-555-0142"
            />
          </Field>
        </div>
        <button
          type="button"
          data-testid="profile-save"
          className={primaryButtonClass}
          disabled={!dirty || saving}
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span>Account type</span>
        <span data-testid="profile-kind" className="text-slate-400">
          {org?.kind ?? '—'}
        </span>
      </div>
      {error ? <span className="text-xs text-amber-300">{error}</span> : null}
    </PortalSection>
  );
}
